// ═══════════════════════════════════════════════════════════════════════
// csv.js — tiny deterministic CSV helpers (no dependencies, no AI)
// ═══════════════════════════════════════════════════════════════════════
// Used by the Price Tool (menu items), Daily Log import/export and the
// integration feed's history upload (lib/feedReport.js). Plain
// RFC-4180-ish: fields with comma/quote/newline are quoted, quotes doubled.
// No AI parsing — predictable, free, and round-trips with what we export.

// Build a CSV string from a header array and an array of row-arrays.
export function toCSV(headers, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.map(esc).join(",");
  const body = (rows || []).map((r) => r.map(esc).join(",")).join("\n");
  return body ? `${head}\n${body}` : head;
}

// Trigger a browser download of `csv` as `filename`.
export function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Parse CSV text into its raw records: an array of rows, each an array of
// untrimmed field strings. Handles quoted fields, "" escaped quotes, CRLF and
// a trailing newline. Blank lines are kept (as [""]), so a record's index + 1
// is its row number when the file is opened in a spreadsheet (barring quoted
// line breaks). Used where the header isn't necessarily the first line
// (lib/feedReport.js); parseCSV below builds on it.
export function parseCSVGrid(text) {
  const s = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const grid = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); grid.push(row); row = []; field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); grid.push(row); }
  return grid;
}

// Parse CSV text into { headers: [...], rows: [{header: value, ...}] }.
// Handles quoted fields, "" escaped quotes, CRLF, and trailing newlines.
export function parseCSV(text) {
  const grid = parseCSVGrid(text);

  // Drop fully-blank lines (e.g. trailing newline rows).
  const lines = grid.filter((r) => r.some((c) => c.trim() !== ""));
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0].map((h) => h.trim());
  const rows = lines.slice(1).map((r) => {
    const o = {};
    headers.forEach((h, idx) => { o[h] = (r[idx] != null ? r[idx] : "").trim(); });
    return o;
  });
  return { headers, rows };
}

// Case/spacing-insensitive lookup of a value from a parsed row object by any
// of several accepted header spellings. Returns "" if none match.
export function pick(rowObj, ...names) {
  const norm = (x) => String(x).toLowerCase().replace(/[^a-z0-9]/g, "");
  const map = {};
  Object.keys(rowObj).forEach((k) => { map[norm(k)] = rowObj[k]; });
  for (const n of names) {
    const key = norm(n);
    if (map[key] != null && map[key] !== "") return map[key];
  }
  return "";
}
